import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import type { Mission } from '../../types';
import { feelingById, feelingByScore, firstName, type MissionReflection } from '../../constants/bridge';
import { skyFor } from '../../constants/sky';
import Glyph from '../glyphs/Glyph';

interface Props {
  patientId: string;
  patientName: string;
}

type MoodRow = { created_at: string; mood_score: number; mood_type?: string | null };
type DailyRow = { created_at: string; mood_score: number };

type Event =
  | { kind: 'reflection'; at: string; r: MissionReflection }
  | { kind: 'daily'; at: string; score: number };

const WEEK = 7 * 864e5;

const dayKey = (iso: string) => new Date(iso).toLocaleDateString('he-IL');
const when = (iso: string) =>
  new Date(iso).toLocaleString('he-IL', { weekday: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * "What came up from home since the session": the therapist's end of the bridge.
 * Reads mission_reflections when the migration has run; otherwise falls back to
 * inner-weather logs and today's missions so the panel is never empty for no reason.
 */
export default function TherapistBridgeTimeline({ patientId, patientName }: Props) {
  const [since, setSince] = useState<string | null>(null);
  const [hasSession, setHasSession] = useState(false);
  const [reflections, setReflections] = useState<MissionReflection[]>([]);
  const [moods, setMoods] = useState<MoodRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: last } = await supabase
        .from('patient_summaries').select('created_at')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false }).limit(1);
      const lastAt: string | undefined = last?.[0]?.created_at;
      // Never look back further than two weeks, even if the last session was long ago.
      const floor = new Date(Date.now() - 2 * WEEK).toISOString();
      const from = lastAt ? (lastAt > floor ? lastAt : floor) : new Date(Date.now() - WEEK).toISOString();

      const [refl, mood, day, mis] = await Promise.all([
        supabase.from('mission_reflections').select('*').eq('user_id', patientId).gte('created_at', from).order('created_at', { ascending: false }),
        supabase.from('mood_logs').select('created_at, mood_score, mood_type').eq('user_id', patientId).gte('created_at', from).order('created_at', { ascending: true }),
        supabase.from('daily_moods').select('created_at, mood_score').eq('user_id', patientId).gte('created_at', from).order('created_at', { ascending: false }),
        supabase.from('missions').select('*').eq('user_id', patientId),
      ]);
      if (cancelled) return;
      setSince(from);
      setHasSession(!!lastAt);
      if (!refl.error && refl.data) {
        setReflections(refl.data);
        // Opening this view is the therapist "seeing" what came back; it lights the patient's clinic window.
        if (refl.data.some((r: MissionReflection) => !r.therapist_seen_at)) {
          supabase.from('mission_reflections').update({ therapist_seen_at: new Date().toISOString() })
            .eq('user_id', patientId).is('therapist_seen_at', null)
            .then(() => {}, () => {});
        }
      }
      if (mood.data) setMoods(mood.data);
      if (day.data) setDaily(day.data.filter((d: DailyRow) => d.created_at && typeof d.mood_score === 'number'));
      if (mis.data) setMissions(mis.data);
      setLoaded(true);
    })().catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, [patientId]);

  // One "sky" tile per day since the session, coloured by that day's average inner weather.
  const sky = useMemo(() => {
    if (!since) return [];
    const byDay = new Map<string, number[]>();
    for (const m of moods) {
      const k = dayKey(m.created_at);
      byDay.set(k, [...(byDay.get(k) ?? []), m.mood_score]);
    }
    for (const r of reflections) {
      const f = feelingById(r.feeling);
      if (!f) continue;
      const k = dayKey(r.created_at);
      byDay.set(k, [...(byDay.get(k) ?? []), f.score]);
    }
    const days: { label: string; key: string; score: number | null }[] = [];
    const start = new Date(since);
    start.setHours(0, 0, 0, 0);
    for (let d = new Date(start); d <= new Date(); d.setDate(d.getDate() + 1)) {
      const k = d.toLocaleDateString('he-IL');
      const v = byDay.get(k);
      days.push({
        key: k,
        label: d.toLocaleDateString('he-IL', { weekday: 'narrow' }),
        score: v ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null,
      });
    }
    return days.slice(-14);
  }, [since, moods, reflections]);

  const events: Event[] = useMemo(() => {
    const list: Event[] = [
      ...reflections.map((r) => ({ kind: 'reflection' as const, at: r.created_at, r })),
      ...daily.map((d) => ({ kind: 'daily' as const, at: d.created_at, score: d.mood_score })),
    ];
    return list.sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [reflections, daily]);

  const doneToday = missions.filter((m) => m.is_completed).length;
  const hearts = reflections.filter((r) => r.therapist_heart_at).length;
  const name = firstName(patientName) || patientName;

  const sendHeart = async (r: MissionReflection) => {
    const at = new Date().toISOString();
    setReflections((prev) => prev.map((x) => (x.id === r.id ? { ...x, therapist_heart_at: at } : x)));
    const { error } = await supabase.from('mission_reflections').update({ therapist_heart_at: at }).eq('id', r.id);
    if (error) setReflections((prev) => prev.map((x) => (x.id === r.id ? { ...x, therapist_heart_at: null } : x)));
  };

  const visible = showAll ? events : events.slice(0, 5);

  // Paint the header with the patient's most recent sky.
  const lastMood = moods[moods.length - 1];
  const lastRefl = reflections[0];
  const latestFeeling =
    lastRefl && (!lastMood || lastRefl.created_at > lastMood.created_at)
      ? feelingById(lastRefl.feeling)
      : lastMood ? (feelingById(lastMood.mood_type) ?? feelingByScore(lastMood.mood_score)) : undefined;
  const tone = skyFor(latestFeeling?.id ?? null, 12);
  const ink = tone.dark ? 'text-white' : 'text-foreground';
  const inkSoft = tone.dark ? 'text-white/85' : 'text-foreground/75';

  return (
    <section className="rounded-3xl border border-border bg-card overflow-hidden" aria-labelledby="bridge-timeline-title">
      <div className="px-5 pt-4 pb-3 border-b border-border" style={{ background: `linear-gradient(${tone.top}, ${tone.bottom})` }}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 id="bridge-timeline-title" className={`font-extrabold flex items-center gap-2 ${ink}`}>
              <Glyph name="sprout" size={22} />
              מה עלה מהבית
            </h3>
            {since && (
              <p className={`text-xs font-medium mt-0.5 ${inkSoft}`}>
                {hasSession ? 'מאז הפגישה ב־' : 'מאז '}
                {new Date(since).toLocaleDateString('he-IL', { day: 'numeric', month: 'long' })}
              </p>
            )}
          </div>
          <dl className="flex gap-4 text-center">
            <div>
              <dt className={`text-[11px] font-medium ${inkSoft}`}>היום</dt>
              <dd className={`text-lg font-extrabold tabular-nums ${ink}`}>{doneToday}/{missions.length}</dd>
            </div>
            <div>
              <dt className={`text-[11px] font-medium ${inkSoft}`}>דיווחים</dt>
              <dd className={`text-lg font-extrabold tabular-nums ${ink}`}>{reflections.length + moods.length}</dd>
            </div>
            <div>
              <dt className={`text-[11px] font-medium ${inkSoft}`}>לבבות</dt>
              <dd className={`text-lg font-extrabold tabular-nums ${ink}`}>{hearts}</dd>
            </div>
          </dl>
        </div>

        {sky.length > 0 && (
          <ol className="flex gap-1.5 mt-3" aria-label="מזג האוויר הפנימי בכל יום">
            {sky.map((d) => {
              const f = feelingByScore(d.score);
              return (
                <li
                  key={d.key}
                  className={`flex-1 min-w-0 rounded-xl py-1.5 flex flex-col items-center gap-0.5 ${f ? 'bg-card/90' : 'bg-card/40'}`}
                  title={`${d.key}: ${f?.label ?? 'אין דיווח'}`}
                >
                  {f ? <Glyph name={f.glyph} size={20} title={f.label} /> : <span className="h-[18px] text-muted-foreground" aria-label="אין דיווח">·</span>}
                  <span className="text-[10px] font-bold text-muted-foreground">{d.label}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="p-4">
        {!loaded && <p className="text-sm text-muted-foreground">טוען...</p>}
        {loaded && events.length === 0 && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            עוד לא עלה כלום מהבית מאז הפגישה. פתק ראשון עם "למה זה חשוב" יכול לפתוח את הגשר.
          </p>
        )}
        <ol className="relative border-s-2 border-dashed border-border ms-2 space-y-3">
          {visible.map((e, i) => {
            if (e.kind === 'daily') {
              return (
                <li key={`d${i}`} className="ps-4 relative">
                  <span className="absolute -start-[7px] top-1.5 w-3 h-3 rounded-full bg-secondary" aria-hidden="true" />
                  <p className="text-sm text-foreground">
                    דירוג יומי: <span className="font-bold tabular-nums">{e.score}/10</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">{when(e.at)}</p>
                </li>
              );
            }
            const f = feelingById(e.r.feeling);
            const hearted = !!e.r.therapist_heart_at;
            return (
              <li key={`r${e.r.id}`} className="ps-4 relative">
                <span className="absolute -start-[7px] top-1.5 w-3 h-3 rounded-full bg-bridge" aria-hidden="true" />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground">
                      <span className="font-bold">{e.r.mission_title}</span>
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      נעשתה · הרגיש/ה
                      {f && <Glyph name={f.glyph} size={14} />}
                      <span className="font-bold text-foreground">{f?.label ?? e.r.feeling}</span>
                      · {when(e.at)}
                    </p>
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => !hearted && sendHeart(e.r)}
                    disabled={hearted}
                    aria-pressed={hearted}
                    aria-label={hearted ? 'נשלח לב' : `לשלוח ל${name} לב על המשימה`}
                    className={`shrink-0 h-9 px-3 rounded-full text-xs font-bold flex items-center gap-1 transition-colors ${
                      hearted ? 'bg-accent/10 text-accent' : 'bg-muted text-foreground hover:bg-accent/10 hover:text-accent'
                    }`}
                  >
                    <Glyph name="heart" size={16} className={hearted ? '' : 'opacity-50 grayscale'} />
                    {hearted ? 'נשלח' : 'מילה טובה'}
                  </motion.button>
                </div>
              </li>
            );
          })}
        </ol>
        {events.length > 5 && (
          <button onClick={() => setShowAll((v) => !v)} className="mt-3 text-sm font-bold text-primary">
            {showAll ? 'פחות' : `עוד ${events.length - 5}`}
          </button>
        )}
      </div>
    </section>
  );
}
